import * as React from 'react';
import { useSelector } from 'react-redux';
import { Button, IconArrowLeft16, IconHighDemand16, Textarea } from '@cian/ui-kit';
import { debounce } from 'throttle-debounce';
import { useFormSubmitHandler } from './hooks';
import { PhoneInput } from '../../../components/PhoneInput';
import { RegionSuggest } from '../../../components/RegionSuggest';
import { FormCardFooterLayout } from '../../../components/RequestFormV2/FormCardFooterLayout';
import { FormCardLayout } from '../../../components/RequestFormV2/FormCardLayout';
import { FormRequestLayout } from '../../../components/RequestFormV2/FormRequestLayout';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { selectAnalyticsUser, selectPage, selectPageExtra } from '../../../selectors/analytics';
import { selectFlightEnabled } from '../../../selectors/meta';
import {
  selectFormPhone,
  selectFormPhoneError,
  selectFormRegion,
  selectFormRequirements,
  selectFormStep,
  selectIsFormSubmitting,
} from '../../../selectors/requestFormV2';
import { selectUserPhone } from '../../../selectors/user';
import { getPopularRegions } from '../../../services/popularRegions';
import { searchRegionSuggests } from '../../../services/regionSuggests';
import {
  setFormPhone,
  setErrors,
  setFormRequirements,
  setFormStep,
  setRegion,
} from '../../../slices/requestFormV2/requestFormV2Slice';
import { IAnalyticsUser, TPage, TPageExtra } from '../../../types/analytics';
import { IRegion } from '../../../types/requestForm';
import { EFormStep } from '../../../types/requestForm/requestFormV2';
import { trackPhoneFill, trackRequirementsFill } from '../../../utils/analytics';
import { useApplicationContext } from '../../../utils/applicationContext';
import { PersonalAgreementContainer } from '../PersonalAgreement';
import { useFormShowAnalytics } from '../utils/useFormShowAnalytics';

const image = 'https://static.cdn-cian.ru/frontend/collections-assets/consulting-request/img-form-desk-4.png';
const imageMobile = 'https://static.cdn-cian.ru/frontend/collections-assets/consulting-request/img-form-mob-4.jpg';

export const RequestFormContainer = () => {
  const popularRegionsRef = React.useRef<IRegion[] | null>(null);
  const dispatch = useAppDispatch();
  const context = useApplicationContext();
  const step = useSelector(selectFormStep);
  const region = useSelector(selectFormRegion);
  const phone = useSelector(selectFormPhone);
  const phoneError = useSelector(selectFormPhoneError);
  const requirements = useSelector(selectFormRequirements);
  const isLoading = useSelector(selectIsFormSubmitting);

  const analyticsUser = useSelector(selectAnalyticsUser);
  const userPhone = useSelector(selectUserPhone);
  const page = useSelector(selectPage);
  const extra = useSelector(selectPageExtra);
  const isFlightEnabled = useSelector(selectFlightEnabled);

  const isFormFilled = phone && region;

  const submitHandler = useFormSubmitHandler();

  useFormShowAnalytics(step === EFormStep.Contacts);

  const debouncedTrackPhoneFill = React.useMemo(
    () =>
      debounce(
        300,
        (userPhone: string | null, phone: string, analyticsUser: IAnalyticsUser, page: TPage, extra: TPageExtra) => {
          trackPhoneFill(userPhone, phone, analyticsUser, page, extra);
        },
      ),
    [],
  );

  const debouncedTrackRequirementsFill = React.useMemo(
    () =>
      debounce(
        300,
        (
          userPhone: string | null,
          phone: string,
          analyticsUser: IAnalyticsUser,
          page: TPage,
          extra: TPageExtra,
          requirements: string | null,
        ) => {
          trackRequirementsFill(userPhone, phone, analyticsUser, page, extra, requirements);
        },
      ),
    [],
  );

  const requirementsAnalytics = React.useCallback(() => {
    debouncedTrackRequirementsFill(userPhone, phone || '', analyticsUser, page, extra, requirements);
  }, [userPhone, analyticsUser, page, extra, phone, debouncedTrackRequirementsFill, requirements]);

  const phoneAnalytics = React.useCallback(() => {
    debouncedTrackPhoneFill(userPhone, phone || '', analyticsUser, page, extra);
  }, [userPhone, analyticsUser, page, extra, phone, debouncedTrackPhoneFill]);

  React.useEffect(() => {
    if (phone) {
      phoneAnalytics();
    }
  }, [phone, phoneAnalytics]);

  React.useEffect(() => {
    if (requirements) {
      requirementsAnalytics();
    }
  }, [requirements, requirementsAnalytics]);

  const handleRegionChange = React.useCallback(
    (region: IRegion | null) => {
      dispatch(setRegion(region));
    },
    [dispatch],
  );

  const handlePhoneChange = React.useCallback(
    (phone: string) => {
      dispatch(setFormPhone(phone));
      dispatch(setErrors({ phone: null }));
    },
    [dispatch],
  );

  const handleRequirementsChange = React.useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      dispatch(setFormRequirements(e.target.value));
    },
    [dispatch],
  );

  const searchRegion = React.useCallback(
    async (text: string): Promise<IRegion[]> => {
      if (text.length >= 3) {
        return searchRegionSuggests(context, { text });
      }

      if (!popularRegionsRef.current) {
        popularRegionsRef.current = await getPopularRegions(context);
      }

      return popularRegionsRef.current;
    },
    [context],
  );

  const handleBack = () => {
    dispatch(setFormStep(EFormStep.AdditionalServices));
  };

  const handleNext = submitHandler;

  return (
    <FormCardLayout
      dataTestId="RequestFormV2Contacts"
      question={'Финальные штрихи!'}
      image={image}
      imageMobile={imageMobile}
      legend={isFlightEnabled ? 'Шаг 4 из 4' : undefined}
    >
      <FormRequestLayout
        region={
          <RegionSuggest
            disabled={false}
            onChange={handleRegionChange}
            onRegionSearch={searchRegion}
            value={region}
            name="region"
            placeholder="Выберите город"
            size="M"
          />
        }
        phone={
          <PhoneInput
            phoneError={phoneError || undefined}
            disabled={false}
            onChange={handlePhoneChange}
            value={phone || ''}
            name="phone"
            size="M"
            invalid={!!phoneError}
          />
        }
        requirements={
          <Textarea
            disabled={false}
            onChange={handleRequirementsChange}
            value={requirements || ''}
            maxLength={300}
            maxRows={5}
            minRows={5}
            name="requirements"
            placeholder="Хочу двушку с высокими потолками в СВАО, чтобы двор был тихий и бассейн рядом"
            size="M"
          />
        }
      />
      <FormCardFooterLayout>
        <Button
          size="M"
          onClick={handleBack}
          theme="stroke_secondary"
          beforeIcon={<IconArrowLeft16 color="icon-primary-default" />}
        />
        {isFormFilled && (
          <Button
            size="M"
            name="Отправить заявку"
            role="button"
            onClick={handleNext}
            fullWidth
            theme="fill_primary"
            loading={isLoading}
            afterIcon={<IconHighDemand16 color="theme-white" />}
          >
            Отправить заявку
          </Button>
        )}
      </FormCardFooterLayout>
      {isFormFilled && <PersonalAgreementContainer />}
    </FormCardLayout>
  );
};
